// src/components/molecules/QuickSelectionMenu/QuickSelectionMenu.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  useTheme
} from '@mui/material';
import {
  SelectAll as SelectAllIcon,
  CheckBoxOutlineBlank as UnreadIcon,
  CheckBox as ReadIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as NotFavoriteIcon,
  SentimentSatisfied as PositiveIcon,
  SentimentNeutral as NeutralIcon,
  SentimentVeryDissatisfied as NegativeIcon,
  Image as ImageIcon,
  ImageNotSupported as NoImageIcon,
  LocalOffer as TagsIcon,
  LocalOfferOutlined as NoTagsIcon,
  Schedule as RecentIcon,
  History as OldIcon,
  KeyboardArrowDown as ArrowIcon
} from '@mui/icons-material';

/**
 * QuickSelectionMenu - Componente molecular para menu de seleção rápida
 * 
 * Este componente oferece opções de seleção rápida por critérios
 * como não lidos, favoritos, sentimento, etc.
 * 
 * CLASSIFICAÇÃO: MOLÉCULA - Combina múltiplos átomos (Button, Menu, MenuItem)
 */
const QuickSelectionMenu = ({
  options = [],
  onSelect,
  disabled = false,
  className = '',
  ...props
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (criteria) => {
    onSelect(criteria);
    handleClose();
  };

  // Mapear ícones para cada critério
  const getIcon = (criteria) => {
    switch (criteria) {
      case 'unread':
        return <UnreadIcon fontSize="small" />;
      case 'read':
        return <ReadIcon fontSize="small" />;
      case 'favorite':
        return <FavoriteIcon fontSize="small" />;
      case 'not_favorite':
        return <NotFavoriteIcon fontSize="small" />;
      case 'positive':
        return <PositiveIcon fontSize="small" />;
      case 'neutral':
        return <NeutralIcon fontSize="small" />;
      case 'negative':
        return <NegativeIcon fontSize="small" />;
      case 'with_image':
        return <ImageIcon fontSize="small" />;
      case 'without_image':
        return <NoImageIcon fontSize="small" />;
      case 'with_tags':
        return <TagsIcon fontSize="small" />;
      case 'no_tags':
        return <NoTagsIcon fontSize="small" />;
      case 'recent':
        return <RecentIcon fontSize="small" />;
      case 'old':
        return <OldIcon fontSize="small" />;
      default:
        return <SelectAllIcon fontSize="small" />;
    }
  };

  // Agrupar opções por categoria
  const groupedOptions = options.reduce((groups, option) => {
    const category = getCategory(option.value);
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(option);
    return groups;
  }, {});

  const getCategory = (value) => {
    if (['read', 'unread'].includes(value)) return 'status';
    if (['favorite', 'not_favorite'].includes(value)) return 'favorites';
    if (['positive', 'neutral', 'negative'].includes(value)) return 'sentiment';
    if (['with_image', 'without_image'].includes(value)) return 'media';
    if (['with_tags', 'no_tags'].includes(value)) return 'tags';
    if (['recent', 'old'].includes(value)) return 'time';
    return 'other';
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'status':
        return 'Status de Leitura';
      case 'favorites':
        return 'Favoritos';
      case 'sentiment':
        return 'Sentimento';
      case 'media':
        return 'Mídia';
      case 'tags':
        return 'Tags';
      case 'time':
        return 'Tempo';
      default:
        return 'Outros';
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleClick}
        disabled={disabled}
        endIcon={<ArrowIcon />}
        className={`quick-selection-menu ${className}`}
        sx={{
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: theme.shadows[2]
          },
          '&.Mui-disabled': {
            borderColor: theme.palette.action.disabled,
            color: theme.palette.action.disabled
          }
        }}
        {...props}
      >
        Seleção Rápida
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 250,
            maxHeight: 400,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[3]
          }
        }}
      >
        {Object.entries(groupedOptions).map(([category, categoryOptions]) => (
          <Box key={category}>
            <MenuItem disabled sx={{ 
              backgroundColor: theme.palette.action.hover,
              '&.Mui-disabled': {
                opacity: 1
              }
            }}>
              <Typography variant="caption" fontWeight={600} color={theme.palette.text.secondary}>
                {getCategoryLabel(category)}
              </Typography>
            </MenuItem>
            
            {categoryOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleSelect(option.value)}
                sx={{ 
                  pl: 3,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                <ListItemIcon sx={{ color: theme.palette.text.secondary }}>
                  {getIcon(option.value)}
                </ListItemIcon>
                <ListItemText 
                  primary={option.label}
                  secondary={option.count ? `${option.count} itens` : undefined}
                  primaryTypographyProps={{
                    color: theme.palette.text.primary
                  }}
                  secondaryTypographyProps={{
                    color: theme.palette.text.secondary
                  }}
                />
              </MenuItem>
            ))}
            
            <Divider sx={{ borderColor: theme.palette.divider }} />
          </Box>
        ))}
      </Menu>
    </>
  );
};

QuickSelectionMenu.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      count: PropTypes.number
    })
  ),
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string
};

export default QuickSelectionMenu;
